import produce from 'immer';
import { sortByNames } from '../components/LeasePricing/ViewUnitLevel/models';
import { AnyAction } from 'redux';
import {
  IN_LEASE_PRICING_ACTIONS,
  NEW_LEASE_ACTIONS,
} from '../actions/leasePricing';
import { ALL } from '../constants';
import { sortBy } from 'lodash';
import { LeasePriceFlagItem } from '../types/flag.model';
import { formattedAmount } from './../utils/index';
import {
  LeasePricingState,
  PropertyWiseUnitCode,
  unitTypeData,
  UnitTypes,
} from '../types/leasePrice.model';

const initialState: LeasePricingState = {
  isLoading: false,
  offersUpdateLoading: false,
  selectedProperty: ALL,
  selectedBedRooms: ALL,
  selectedUnitType: ALL,
  selectedShowAs: 2,
  selectedSortOption: `${sortByNames.AvailableUnits}_desc`,
  selectedUnitTypeKey: undefined,
  unitTypes: undefined,
  leasePriceDetails: undefined,
  leasePriceKeys: undefined,
  leasePriceSummary: undefined,
  summaryLength: 6,
  searchPhrase: '',
  activeStrategy: undefined,
  limit: 10,
  offset: 0,
  pageCount: 0,
  offerState: 'InReview',
  isAvailable: 'Available',
  priceMatrices: new Map(),
  priceMatricesLoading: new Map(),
  offerCycles: undefined,
  unitDetails: undefined,
  unitLevelPagination: {
    pageCount: 0,
    offset: 0,
    limit: 10,
  },
  editDialogProps: undefined,
  leasePricingModule: undefined,
  searchUnitIdQuery: '',
  count: 0,
};

const leasePricingReducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case IN_LEASE_PRICING_ACTIONS.FETCH_OFFER_CYCLES: {
      return produce(state, (df) => {
        df.offerCycles = action.payload;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.FETCH_UNIT_DETAILS: {
      return produce(state, (df) => {
        df.unitDetails = action.payload.unitLevelData;
        df.unitLevelPagination.pageCount =
          action.payload.unitLevelData.length / df.unitLevelPagination.limit;
        df.count = action.payload.unitLevelData.length;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.FETCH_UNIT_PRICE_MATRIX: {
      return produce(state, (df) => {
        const unitDetailId = action.payload.UnitTypeKey;
        const unitDetail = df.unitDetails?.find((el) => el.id === unitDetailId);
        if (unitDetail)
          unitDetail.priceMatrices = action.payload.priceMatricesData;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.IS_LOADING: {
      return produce(state, (df) => {
        df.isLoading = action.payload;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.OFFERS_UPDATE_LOADING: {
      return produce(state, (df) => {
        df.offersUpdateLoading = action.payload;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_SUMMARY_LENGTH: {
      return produce(state, (df) => {
        df.summaryLength = action.payload.length;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_LEASE_PRICE_SUMMARY: {
      return produce(state, (df) => {
        df.leasePriceSummary = action.payload;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_SELECTED_UNIT_TYPE: {
      return produce(state, (df) => {
        df.selectedUnitType = String(action.payload);
        df.offset = 0;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.RESET_PRICE_MATRICES: {
      return produce(state, (df) => {
        df.priceMatrices = new Map();
        df.priceMatricesLoading = new Map();
      });
    }
    case IN_LEASE_PRICING_ACTIONS.UPDATE_BEST_OFFER: {
      return produce(state, (df) => {
        const { leaseKey, shiftAmount, shiftBy } = action.payload;

        const leasePriceKey =
          df.leasePriceKeys?.entities.leasePriceKeys[leaseKey];

        const updateValue =
          shiftBy === 'usd'
            ? shiftAmount
            : (leasePriceKey.bestOffer * shiftAmount) / 100;

        let NewLeasePrice;

        if (df.selectedUnitTypeKey && df.leasePriceDetails) {
          NewLeasePrice =
            df.leasePriceDetails?.entities.leasePriceDetails[
              df.selectedUnitTypeKey
            ].NewLeasePrice;

          NewLeasePrice.map((el) => (el.Price += updateValue));

          df.leasePriceDetails.entities.leasePriceDetails[
            df.selectedUnitTypeKey
          ].NewLeasePrice = NewLeasePrice;
        }

        leasePriceKey.bestOffer += updateValue;

        if (df.leasePriceKeys)
          df.leasePriceKeys.entities.leasePriceKeys[leaseKey] = leasePriceKey;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_SELECTED_PROPERTY: {
      return produce(state, (df) => {
        df.selectedProperty = action.payload;
        df.selectedUnitType = ALL;
        df.offset = 0;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_SELECTED_BEDROOMS: {
      return produce(state, (df) => {
        df.selectedBedRooms = action.payload;
        df.offset = 0;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_SEARCH_PHRASE: {
      return produce(state, (df) => {
        df.searchPhrase = action.payload;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_SELECTED_SORT_OPTION: {
      return produce(state, (df) => {
        df.selectedSortOption = action.payload;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_SELECTED_SHOW_AS: {
      return produce(state, (df) => {
        df.selectedShowAs = action.payload;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_LIMIT: {
      return produce(state, (df) => {
        df.limit = Number(action.payload);
        df.offset = 0;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_OFFSET: {
      return produce(state, (df) => {
        df.offset = Number(action.payload);
      });
    }

    case IN_LEASE_PRICING_ACTIONS.SET_UNIT_LEVEL_PAGINATION_LIMIT: {
      return produce(state, (df) => {
        df.unitLevelPagination.limit = Number(action.payload);
        df.unitLevelPagination.offset = 0;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_UNIT_LEVEL_PAGINATION_OFFSET: {
      return produce(state, (df) => {
        df.unitLevelPagination.offset = Number(action.payload);
      });
    }

    case IN_LEASE_PRICING_ACTIONS.SET_SELECTED_UNIT_KEY: {
      return produce(state, (df) => {
        df.selectedUnitTypeKey = action.payload;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_ALL_UNIT_TYPES: {
      return produce(state, (df) => {
        const unitTypes: PropertyWiseUnitCode = {};
        for (const el of action.payload) {
          const unitTypesArr: UnitTypes[] = el.unit_types.map((val: any) => {
            return {
              name: `${val.Name} (${val.Unit_Type})`,
              unitType_code: val.Unit_Type,
            };
          });
          unitTypes[el.Property_Code] = unitTypesArr;
        }
        df.unitTypes = unitTypes;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_ALL_UNIT_TYPE_DETAILS: {
      return produce(state, (df) => {
        const {
          normalized: [data1, data2],
          strategy,
          count,
        } = action.payload;
        df.leasePriceDetails = data2;
        df.leasePriceKeys = data1;
        df.selectedUnitTypeKey = data1.result[0];
        df.activeStrategy = strategy;
        df.pageCount = Math.ceil(count / df.limit);
        df.count = count;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.ADJUST_PRICE: {
      return produce(state, (df) => {
        const { leaseKey, property_code, unit_Type, term, adjustedPrice } =
          action.payload;
        if (df.leasePriceDetails) {
          const leasePriceKey =
            df.leasePriceKeys?.entities.leasePriceKeys[leaseKey];

          const newLeasePrices =
            df.leasePriceDetails?.entities.leasePriceDetails[leaseKey]
              .NewLeasePrice;

          const leasePriceIndex = newLeasePrices.findIndex((ele: any) => {
            return (
              ele.Property_Code === property_code &&
              leasePriceKey.UnitType === unit_Type &&
              ele.Term === term
            );
          });

          if (leasePriceIndex !== -1)
            df.leasePriceDetails.entities.leasePriceDetails[
              leaseKey
            ].NewLeasePrice[leasePriceIndex].Price = adjustedPrice;

          let newBestTerm = sortBy(newLeasePrices, (obj: any) => obj.Price)[0];

          leasePriceKey.bestTerm = newBestTerm.Term;
          leasePriceKey.bestOffer = newBestTerm.Price;
          if (df.leasePriceKeys)
            df.leasePriceKeys.entities.leasePriceKeys[leaseKey] = leasePriceKey;

          // UPDATE VALUES IN MATRICES /////////////////////////////////////
          let matricesItemToUpdate: any = { ...df.priceMatrices.get(leaseKey) };

          if (Object.keys(matricesItemToUpdate).length) {
            matricesItemToUpdate.terms = matricesItemToUpdate.terms.map(
              (item, i) => {
                let newItem = item.split(' ');
                newItem = newItem[0];
                if (term === Number(newItem)) {
                  matricesItemToUpdate.prices[0][i] =
                    formattedAmount(adjustedPrice);
                  let oldBestPrice = Number(
                    matricesItemToUpdate.bestPrices[0]
                      .replace('$', '')
                      .replace(',', '')
                  );
                  if (oldBestPrice >= Number(adjustedPrice)) {
                    matricesItemToUpdate.bestPrices[0] =
                      formattedAmount(adjustedPrice);
                  } else {
                    let pricesForEearliestTerm: Array<number> = [];
                    matricesItemToUpdate.prices[0].forEach(
                      (item: string | number) => {
                        let itemPrice = Number(
                          String(item).replace('$', '').replace(',', '')
                        );
                        pricesForEearliestTerm.push(itemPrice);
                      }
                    );
                    matricesItemToUpdate.bestPrices[0] = Math.min(
                      ...pricesForEearliestTerm
                    );
                    matricesItemToUpdate.bestPrices[0] = formattedAmount(
                      matricesItemToUpdate.bestPrices[0]
                    );
                  }
                }
                return item;
              }
            );
            df.priceMatrices.set(leaseKey, matricesItemToUpdate);
          }
          ///////////////////////////
        }
      });
    }
    case IN_LEASE_PRICING_ACTIONS.APPROVE: {
      return produce(state, (df) => {
        const { leaseKey } = action.payload;
        if (df.leasePriceKeys && df.offerState !== undefined) {
          delete df.leasePriceKeys.entities.leasePriceKeys[leaseKey];
        }

        if (df.leasePriceKeys && df.offerState === undefined) {
          const leaseInfo = df.leasePriceKeys.entities.leasePriceKeys[leaseKey];
          leaseInfo.IsApproved = !leaseInfo.IsApproved;
          df.leasePriceKeys.entities.leasePriceKeys[leaseKey] = leaseInfo;
        }
      });
    }
    case IN_LEASE_PRICING_ACTIONS.APPROVE_UNIT_PRICE: {
      return produce(state, (df) => {
        const { unitDetailId, isApprovedFlag } = action.payload;
        df.unitDetails!.map((item: IUnitLevelItem) => {
          if (item.id === unitDetailId) {
            item.IsApproved = isApprovedFlag;
            item.IsExcluded = false;
          }

          return item;
        });
      });
    }
    case IN_LEASE_PRICING_ACTIONS.EXCLUDE: {
      return produce(state, (df) => {
        const { leaseKey } = action.payload;
        if (df.leasePriceKeys && df.offerState !== undefined) {
          delete df.leasePriceKeys.entities.leasePriceKeys[leaseKey];
        }

        if (df.leasePriceKeys && df.offerState === undefined) {
          const leaseInfo = df.leasePriceKeys.entities.leasePriceKeys[leaseKey];
          leaseInfo.IsExcluded = !leaseInfo.IsExcluded;
          df.leasePriceKeys.entities.leasePriceKeys[leaseKey] = leaseInfo;
        }
      });
    }

    case IN_LEASE_PRICING_ACTIONS.EXCLUDE_UNIT_PRICE: {
      return produce(state, (df) => {
        const { unitDetailId, isExcludedFlag } = action.payload;
        df.unitDetails?.map((item: IUnitLevelItem) => {
          if (item.id === unitDetailId) {
            item.IsApproved = false;
            item.IsExcluded = isExcludedFlag;
          }

          return item;
        });
      });
    }

    case IN_LEASE_PRICING_ACTIONS.SET_SELECTED_OFFER_STATE: {
      return produce(state, (df) => {
        df.offerState = action.payload;
      });
    }

    case IN_LEASE_PRICING_ACTIONS.SET_IS_OCCUPIED_STATE: {
      return produce(state, (df) => {
        df.isAvailable = action.payload;
      });
    }

    case IN_LEASE_PRICING_ACTIONS.SET_PRICE_MATRICES: {
      return produce(state, (df) => {
        const { priceMatricesData, UnitTypeKey } = action.payload;
        df.priceMatrices.set(UnitTypeKey, priceMatricesData);
      });
    }
    case IN_LEASE_PRICING_ACTIONS.PRICE_MATRICES_IS_LOADING: {
      return produce(state, (df) => {
        const { isLoading, UnitTypeKey } = action.payload;
        df.priceMatricesLoading.set(UnitTypeKey, isLoading);
      });
    }
    case 'NEW_LEASE_UPDATE_FLAG': {
      return produce(state, (df) => {
        const flagItem = action.payload as LeasePriceFlagItem;
        if (df.leasePriceKeys) {
          const leaseInfo = df.leasePriceKeys.entities.leasePriceKeys[
            flagItem.leaseKey
          ] as unitTypeData;
          leaseInfo.IsFlagged = !flagItem.resolved;
          leaseInfo.FlagDetails = flagItem.id;
          df.leasePriceKeys.entities.leasePriceKeys[flagItem.leaseKey] =
            leaseInfo;
        }
      });
    }
    case 'NEW_LEASE_BY_UNIT_UPDATE_FLAG': {
      return produce(state, (df) => {
        const flagItem = action.payload;
        if (df.unitDetails) {
          const unitDetail = df.unitDetails.find(
            (unit) => unit.id === flagItem.info.unitDetailId
          );
          if (unitDetail) {
            unitDetail.IsFlagged = !flagItem.resolved;
            unitDetail.FlagItem = flagItem;
          }
        }
      });
    }
    case NEW_LEASE_ACTIONS.SET_SHOW_EDIT_DIALOG: {
      return produce(state, (df) => {
        df.editDialogProps = action.payload;
      });
    }
    case NEW_LEASE_ACTIONS.UPDATE_UNIT_OFFERS: {
      return produce(state, (df) => {
        const { priceMatrices, unitDetailId } = action.payload;

        const unitDetail = df.unitDetails?.find((ud) => ud.id === unitDetailId);

        if (unitDetail) {
          unitDetail.BestOfferTerm = parseInt(
            priceMatrices.terms.toReversed()[
              priceMatrices.prices[0]
                .toReversed()
                .findIndex(
                  (item: string) => item === priceMatrices.bestPrices[0]
                )
            ]
          );

          unitDetail.BestOfferPrice = priceMatrices.bestPrices[0]
            .replace('$', '')
            .replace(',', '');

          let twelveMonthOffer =
            priceMatrices.prices[0][
              priceMatrices.terms.findIndex(
                (term: string) => parseInt(term) === 12
              )
            ];
          unitDetail.TwelveMonthPrice = twelveMonthOffer
            .replace('$', '')
            .replace(',', '');

          unitDetail.priceMatrices = priceMatrices;
        }
      });
    }

    case NEW_LEASE_ACTIONS.SET_LEASE_PRICING_MODULE: {
      return produce(state, (df) => {
        df.leasePricingModule = action.payload;
        if (action.payload === 'UnitType') {
          df.selectedSortOption = `${sortByNames.AvailableUnits}_desc`;
        } else if (action.payload === 'Unit') {
          df.selectedSortOption = `${sortByNames.DaysVacant}_desc`;
        } else {
          df.selectedSortOption = undefined;
        }
      });
    }

    case NEW_LEASE_ACTIONS.UPDATE_SEARCH_UNITID_QUERY: {
      return { ...state, searchUnitIdQuery: action.payload };
    }
    case 'UPDATE_UNIT_LIST_COUNT': {
      return produce(state, (df) => {
        df.count = action.payload;
      });
    }
    // default states
    case 'INITIALIZE': {
      return state;
    }
    case 'LOGOUT': {
      return initialState;
    }
    default: {
      return state;
    }
  }
};

export default leasePricingReducer;
